/**
 * @fileoverview Shared type contracts for the ParcelDataPanel — the unified
 *   parcel data management surface used by both system-web (system-admin mode)
 *   and tenant-web (department-admin mode). Each host provides a data adapter
 *   that maps its own Dexie-backed hooks into this unified shape.
 * @module ui-admin/parcel-data-panel/types
 *
 * Responsibilities:
 * - Define the unified data shape (ParcelDataPanelData) that both hosts
 *   project their Dexie reads into.
 * - Define the mode-specific action callbacks (ParcelDataPanelActions) that
 *   each host implements.
 * - Define the shared sub-shapes (DataQuality, ImportHistoryRow, etc.) used
 *   by the panel's sub-components.
 *
 * Dependencies:
 * - None (pure type module).
 *
 * Architecture:
 * - Host adapter pattern: the shared ParcelDataPanel component accepts
 *   `data: ParcelDataPanelData` and `actions: ParcelDataPanelActions` as
 *   props. Each host (system-web, tenant-web) creates its own hook that
 *   reads from its own Dexie tables and maps the results into this shape.
 *   This follows the same pattern as ContactsModulePanel +
 *   ContactsModuleConductorAdapter.
 *
 * Notes:
 * - The data shape is a superset of what both system-web and tenant-web
 *   need. Fields that are only available in one host are nullable so the
 *   panel can gracefully omit sections when data is absent.
 * - `mode` determines which action buttons and editing capabilities are
 *   rendered. 'system-admin' has full control (token editing, admin bypass,
 *   reconfigure with file selection). 'department-admin' can check for
 *   updates, run updates (consuming a token), and toggle create-owner-
 *   contacts, but cannot change the source file or reconfigure the county.
 */

// ---------------------------------------------------------------------------
// Shared sub-shapes
// ---------------------------------------------------------------------------

/**
 * @contract DataQuality
 * Purpose: Four data-quality coverage percentages for a parcel dataset.
 * Fields:  ownerNamePercent, mailingAddressPercent, situsAddressPercent,
 *          geometryValidPercent, unavailableMetrics (optional).
 * Rules:   Each percent field is 0-100 or null when unavailable. Used for
 *          both file-level quality (from the cache file metadata) and
 *          import-level quality (from the import run statistics).
 *          `unavailableMetrics` is an optional list of metric names that
 *          could not be computed for this dataset (e.g. due to missing
 *          columns in the source file). Only the tenant-web import-level
 *          quality surfaces this today; system-web's cache file and import
 *          history shapes do not carry it, so it is optional.
 * Sync:    N/A — this is a presentation shape projected from host data.
 */
export interface DataQuality {
  ownerNamePercent: number | null;
  mailingAddressPercent: number | null;
  situsAddressPercent: number | null;
  geometryValidPercent: number | null;
  unavailableMetrics?: string[];
}

/**
 * @contract ImportHistoryRow
 * Purpose: One row in the import history table.
 * Fields:  _id, date, parcels, newCount, updatesCount, duration, version,
 *          dataQuality.
 * Rules:   `date` is an ISO string. Counts are nullable when unknown.
 *          `dataQuality` is the import-level quality for that run (may be
 *          null for older runs that did not capture quality metrics).
 * Sync:    N/A — presentation shape projected from host data.
 */
export interface ImportHistoryRow {
  _id: string;
  date: string;
  parcels: number | null;
  newCount: number | null;
  updatesCount: number | null;
  duration: string | null;
  version: string | null;
  dataQuality: DataQuality | null;
}

/**
 * @reusable isParcelImportRun
 * Usage: Classify system import-run documents consistently in every Parcel Data host adapter.
 * Rules: Presentation-only; explicit non-Regrid markers exclude a run, while legacy unmarked runs remain included.
 */
export function isParcelImportRun(run: Record<string, unknown>): boolean {
  const vendorHint = typeof run.vendorHint === 'string' ? run.vendorHint.toLowerCase() : '';
  const sourceSystem = typeof run.sourceSystem === 'string' ? run.sourceSystem.toLowerCase() : '';
  const dataMode = typeof run.dataMode === 'string' ? run.dataMode.toLowerCase() : '';
  return !(
    (vendorHint && vendorHint !== 'regrid')
    || (sourceSystem && sourceSystem !== 'regrid')
    || (dataMode && dataMode !== 'parcel')
  );
}

// ---------------------------------------------------------------------------
// Configuration
// ---------------------------------------------------------------------------

/**
 * @contract ParcelDataConfig
 * Purpose: The department's parcel data configuration — how parcels are
 *   sourced and filtered for this department.
 * Fields:  path, regridFileId, regridFileName, filterMode, hazardLevels,
 *          createOwnerContacts, fipsCode, county, state.
 * Rules:   `path` is 'regrid' | 'manual' | 'skip' | null. `filterMode` is
 *          'all' | 'department' | 'fireHazard' | null. `createOwnerContacts`
 *          is a strict boolean. `fipsCode` is the 5-digit county FIPS.
 * Sync:    System-authoritative; read-only from tenant-web. Written by
 *          onboarding, system-api admin routes, and the Regrid worker.
 */
export interface ParcelDataConfig {
  path: string | null;
  regridFileId: string | null;
  regridFileName: string | null;
  filterMode: string | null;
  hazardLevels: string[];
  createOwnerContacts: boolean;
  fipsCode: string | null;
  county: string | null;
  state: string | null;
}

// ---------------------------------------------------------------------------
// Source file
// ---------------------------------------------------------------------------

/**
 * @contract ParcelSourceFile
 * Purpose: Metadata about the Regrid master-cache GeoParquet file matched
 *   to the department's jurisdiction.
 * Fields:  blobUri, filename, county, state, fipsCode, versionDate,
 *          sizeBytes, featureCount, status, dataQuality.
 * Rules:   `dataQuality` is the FILE-level quality (from the cache file
 *          metadata, not from an import run). `status` is the cache file
 *          processing status (pending/running/completed/failed/cancelled).
 * Sync:    System-authoritative; synced to both system-web (geo_cache_files)
 *          and tenant-web (via system_tenant_ref projection).
 */
export interface ParcelSourceFile {
  blobUri: string | null;
  filename: string | null;
  county: string | null;
  state: string | null;
  fipsCode: string | null;
  versionDate: string | null;
  sizeBytes: number | null;
  featureCount: number | null;
  status: string | null;
  dataQuality: DataQuality | null;
}

// ---------------------------------------------------------------------------
// Import statistics
// ---------------------------------------------------------------------------

/**
 * @contract ParcelImportStats
 * Purpose: Statistics from the most recent parcel data import for this
 *   department — the actual imported subset, not the full file.
 * Fields:  importedAt, parcelsMatched, newParcelsAdded, ownershipChanges,
 *          duration, filterMode, hazardLevels, version, dataQuality.
 * Rules:   `dataQuality` is the IMPORT-level quality (from the import run
 *          statistics, reflecting the quality of the parcels actually
 *          imported into this department — which may differ from the file-
 *          level quality because the department gets a filtered subset).
 *          This is the quality the user specifically asked to surface at
 *          the department level.
 * Sync:    System-authoritative; written by the Regrid ingestion worker.
 */
export interface ParcelImportStats {
  importedAt: string | null;
  parcelsMatched: number | null;
  newParcelsAdded: number | null;
  ownershipChanges: number | null;
  duration: string | null;
  filterMode: string | null;
  hazardLevels: string[];
  version: string | null;
  dataQuality: DataQuality | null;
}

/**
 * @reusable projectParcelImportStats
 * Usage: The single projection from the persisted `regridImportStatistics`
 *   document (written by the Regrid ingestion worker via
 *   persistRegridImportStatistics) into the panel's ParcelImportStats shape.
 *   Both host adapters call this — system-web reads the statistics off the
 *   tenant document, tenant-web reads it off system_tenant_ref, but the
 *   underlying document is the SAME persisted ImportStatistics shape, so the
 *   projection must not be duplicated per host.
 * Rules: Presentation-only pure function; no side effects.
 *   - Returns null when there are no statistics (department never imported).
 *   - `filterMode` / `hazardLevels` come from the STATISTICS, not from the
 *     current parcel config. The statistics record the filter that was
 *     actually applied for that import; the config may have been changed
 *     since, and showing the config's filter next to an older import's
 *     counts would misrepresent what was imported.
 *   - `dataQuality` is the IMPORT-level quality — the quality of the parcels
 *     actually imported into this department. Since the ingestion worker now
 *     computes it over the filtered subset, it legitimately differs from the
 *     file-level quality on ParcelSourceFile. Never substitute one for the
 *     other.
 *   - `versionFallback` (the matched cache file's versionDate, the file's
 *     internal data vintage) is preferred over `azureBlobVersionId`, which
 *     can be null when the worker did not receive the version in its job
 *     payload.
 */
export function projectParcelImportStats(
  stats: Record<string, unknown> | null | undefined,
  versionFallback?: string | null,
): ParcelImportStats | null {
  if (!stats) return null;

  const num = (value: unknown): number | null => (typeof value === 'number' ? value : null);
  const str = (value: unknown): string | null => (typeof value === 'string' ? value : null);

  const rawQuality = stats.dataQuality as Record<string, unknown> | undefined;
  const dataQuality: DataQuality | null = rawQuality
    ? {
      ownerNamePercent: num(rawQuality.ownerNamePercent),
      mailingAddressPercent: num(rawQuality.mailingAddressPercent),
      situsAddressPercent: num(rawQuality.situsAddressPercent),
      geometryValidPercent: num(rawQuality.geometryValidPercent),
      unavailableMetrics: Array.isArray(rawQuality.unavailableMetrics)
        ? rawQuality.unavailableMetrics.map(String)
        : [],
    }
    : null;

  const durationSeconds = num(stats.importDurationSeconds);

  return {
    importedAt: str(stats.importedAt),
    parcelsMatched: num(stats.districtParcelsMatched),
    newParcelsAdded: num(stats.newParcelsAdded),
    ownershipChanges: num(stats.ownershipChangesDetected),
    duration: durationSeconds !== null ? `${Math.round(durationSeconds)}s` : null,
    filterMode: str(stats.filterMode),
    hazardLevels: Array.isArray(stats.filterLevel) ? stats.filterLevel.map(String) : [],
    version: versionFallback ?? str(stats.azureBlobVersionId),
    dataQuality,
  };
}

// ---------------------------------------------------------------------------
// Token entitlement
// ---------------------------------------------------------------------------

/**
 * @contract ParcelTokenEntitlement
 * Purpose: The department's update-token entitlement and current usage.
 * Fields:  tokensPerYear, tokensUsed, remaining, carriedOverBalance,
 *          renewalStart, renewalEnd, currentVersion, lastUpdateDate,
 *          hasUpdate, latestVersion.
 * Rules:   `remaining` = tokensPerYear - tokensUsed + carriedOverBalance.
 *          `hasUpdate` and `latestVersion` come from the check-update
 *          endpoint (null when check-update has not been called).
 * Sync:    System-authoritative; `hasUpdate`/`latestVersion` from the
 *          update_token_usage cache (tenant-web) or null (system-web).
 */
export interface ParcelTokenEntitlement {
  tokensPerYear: number;
  tokensUsed: number;
  remaining: number;
  carriedOverBalance: number;
  renewalStart: string | null;
  renewalEnd: string | null;
  currentVersion: string | null;
  lastUpdateDate: string | null;
  hasUpdate: boolean;
  latestVersion: string | null;
}

// ---------------------------------------------------------------------------
// Owner-data freshness
// ---------------------------------------------------------------------------

/**
 * @contract ParcelOwnerDataSource
 * Purpose: Which writer last refreshed the department's parcel ownership
 *   data (plan §C.4/C.5 — ownership.sourceAsOf provenance).
 * Values:
 * - 'regrid': the Regrid ingestion pipeline (entity ownership.source
 *   'imported' / the tenant's regridImportStatistics).
 * - 'department-csv': a parcelOwnerUpdate ownership import run (entity
 *   ownership.source 'parcelOwnerUpdate').
 * - 'field': a field-collected owner edit (entity ownership.source
 *   'field').
 * Sync: N/A — presentation shape projected from host data.
 */
export type ParcelOwnerDataSource = 'regrid' | 'department-csv' | 'field';

/** User-facing labels for ParcelOwnerDataSource values. */
export const OWNER_DATA_SOURCE_LABELS: Record<ParcelOwnerDataSource, string> = {
  regrid: 'Regrid',
  'department-csv': 'Department CSV',
  field: 'Field edit',
};

/**
 * @contract ParcelOwnerDataUpdate
 * Purpose: The most recent owner-data refresh for the department — when it
 *   happened (the writer's freshness claim: ownership.sourceAsOf / run
 *   dataAsOf / import timestamp) and which source produced it.
 * Fields:  date (ISO string), source.
 * Rules:   `date` is the freshest claim across all sources; `source` names
 *          the writer that produced it. Null when no owner data exists.
 * Sync:    N/A — presentation shape projected from host data.
 */
export interface ParcelOwnerDataUpdate {
  date: string | null;
  source: ParcelOwnerDataSource | null;
}

/**
 * @reusable resolveOwnerDataUpdate
 * Usage: Merge the per-source "last owner-data update" candidates each host
 *   adapter assembles (regrid import timestamp, latest completed
 *   parcelOwnerUpdate run's dataAsOf, max field-edit ownership.sourceAsOf)
 *   into a single ParcelOwnerDataUpdate for the panel.
 * Rules: Presentation-only pure function; ISO strings compare
 *   lexicographically. Candidates with no date are ignored; returns null
 *   when no candidate carries a date.
 */
export function resolveOwnerDataUpdate(
  candidates: Array<{ date: string | null | undefined; source: ParcelOwnerDataSource }>,
): ParcelOwnerDataUpdate | null {
  let best: ParcelOwnerDataUpdate | null = null;
  for (const candidate of candidates) {
    if (!candidate.date) continue;
    if (!best || !best.date || candidate.date > best.date) {
      best = { date: candidate.date, source: candidate.source };
    }
  }
  return best;
}

// ---------------------------------------------------------------------------
// Unified data shape
// ---------------------------------------------------------------------------

/**
 * @contract ParcelDataPanelData
 * Purpose: The unified data shape that both system-web and tenant-web
 *   project their Dexie reads into. The ParcelDataPanel component renders
 *   from this shape exclusively — it never reads Dexie or calls APIs.
 * Fields:  config, sourceFile, importStats, tokens, importHistory,
 *          isLoading, error, departmentName.
 * Rules:   All fields are nullable so the panel can render partial state
 *          (e.g. before sync completes). `error` is a user-facing string.
 * Sync:    N/A — this is a presentation shape; hosts populate it from
 *          their own Dexie-backed hooks.
 */
export interface ParcelDataPanelData {
  config: ParcelDataConfig | null;
  sourceFile: ParcelSourceFile | null;
  importStats: ParcelImportStats | null;
  tokens: ParcelTokenEntitlement | null;
  importHistory: ImportHistoryRow[];
  /**
   * Most recent owner-data refresh (plan §C.5) — resolved by the host
   * adapter via `resolveOwnerDataUpdate` from regridImportStatistics,
   * parcelOwnerUpdate import runs, and entity ownership stamps. Null when
   * the host has no owner-data signal.
   */
  ownerDataUpdate: ParcelOwnerDataUpdate | null;
  isLoading: boolean;
  error: string | null;
  departmentName: string | null;
}

// ---------------------------------------------------------------------------
// Action callbacks
// ---------------------------------------------------------------------------

/**
 * @contract ParcelConfigUpdate
 * Purpose: The fields a system admin can save when reconfiguring parcel
 *   data for a department. This is the full config form payload.
 * Fields:  path, regridFileId, regridFileName, filterMode, hazardLevels,
 *          createOwnerContacts, tokensPerYear.
 * Rules:   Only used in system-admin mode. Department-admin mode uses
 *          toggleCreateContacts instead.
 * Sync:    Written via system-api PUT /departments/:id/parcel-config.
 */
export interface ParcelConfigUpdate {
  path: 'regrid';
  regridFileId: string;
  regridFileName: string;
  filterMode: 'all' | 'department' | 'fireHazard';
  hazardLevels: string[];
  createOwnerContacts: boolean;
  tokensPerYear: number;
}

/**
 * @contract ParcelConfigOverride
 * Purpose: Optional overrides for an admin-bypass import trigger.
 * Fields:  filterMode, hazardLevels, createOwnerContacts.
 * Rules:   When omitted, the department's saved config is used.
 * Sync:    Written via system-api POST /departments/:id/trigger-import.
 */
export interface ParcelConfigOverride {
  filterMode?: 'all' | 'department' | 'fireHazard';
  hazardLevels?: string[];
  createOwnerContacts?: boolean;
}

/**
 * @contract ParcelUpdateResult
 * Purpose: Result of a department-admin "Update Import" action.
 * Fields:  message, runId, status, tokenConsumed, tokensRemaining,
 *          currentVersion.
 * Rules:   `status` is 'queued' | 'up_to_date' | 'no_tokens'.
 * Sync:    Returned by tenant-api POST /api/parcel-data/update.
 */
export interface ParcelUpdateResult {
  message: string;
  runId: string | null;
  status: 'queued' | 'up_to_date' | 'no_tokens';
  tokenConsumed: boolean;
  tokensRemaining: number;
  currentVersion: string | null;
}

/**
 * @contract ParcelEstimateResult
 * Purpose: Result of a parcel count estimate within the department boundary.
 * Fields:  matchedCount, sourceCount.
 * Rules:   Online-only operation (DuckDB COUNT with ST_Intersects).
 * Sync:    N/A — transient result, not persisted.
 */
export interface ParcelEstimateResult {
  matchedCount: number;
  sourceCount: number;
}

/**
 * @contract ParcelDataPanelActions
 * Purpose: Mode-specific action callbacks that each host implements.
 * Fields:  clearError (common), plus mode-specific actions.
 * Rules:   Actions not available in a given mode are undefined and the
 *          panel omits the corresponding buttons. The panel MUST check
 *          for action existence before rendering a button.
 * Sync:    Each host wires actions to its own service layer.
 */
export interface ParcelDataPanelActions {
  // ── Common ──
  clearError?: () => void;

  // ── System-admin actions ──
  /** Save full parcel config (system-admin reconfigure). */
  saveParcelConfig?: (config: ParcelConfigUpdate) => Promise<boolean>;
  /** Trigger an admin-bypass import (no token consumed). Returns jobId. */
  triggerImport?: (overrides?: ParcelConfigOverride) => Promise<string | null>;
  /** Cancel an active import job. */
  cancelImport?: () => Promise<boolean>;
  /** Edit the tokensPerYear entitlement. */
  saveTokenEntitlement?: (tokensPerYear: number) => Promise<boolean>;
  /** Record an admin bypass update (advances lastUpdateDate/currentVersion). */
  adminBypassUpdate?: () => Promise<boolean>;
  /** Refresh cache file metadata from the server. */
  refreshCacheFiles?: () => Promise<void>;
  /** Estimate parcel count within the department boundary. */
  estimateParcels?: () => Promise<ParcelEstimateResult | null>;

  // ── Department-admin actions ──
  /** Check for a newer Regrid file version (does not consume a token). */
  checkForUpdate?: () => Promise<void>;
  /** Run a parcel data update (consumes a token if a newer version exists). */
  runUpdate?: () => Promise<ParcelUpdateResult>;
  /** Toggle createOwnerContacts (department admin — no file change). */
  toggleCreateContacts?: (enabled: boolean) => Promise<boolean>;
}

// ---------------------------------------------------------------------------
// Panel mode
// ---------------------------------------------------------------------------

/**
 * @contract ParcelDataPanelMode
 * Purpose: Determines which action buttons and editing capabilities are
 *   rendered.
 * Values:
 * - 'system-admin': Full control — token editing, admin bypass update,
 *   reconfigure with file selection, estimate parcels, refresh cache files.
 * - 'department-admin': Check for updates, run update (consumes token),
 *   toggle create-owner-contacts. Cannot change source file or reconfigure.
 * Rules: The panel MUST NOT render admin-only actions in department-admin
 *   mode, and MUST NOT render department-only actions in system-admin mode.
 */
export type ParcelDataPanelMode = 'system-admin' | 'department-admin';

// ---------------------------------------------------------------------------
// Filter mode labels (shared)
// ---------------------------------------------------------------------------

export const FILTER_MODE_LABELS: Record<string, string> = {
  all: 'All parcels (no filtering)',
  none: 'No filtering (all parcels)',
  department: 'Department boundary',
  fireHazard: 'Fire hazard overlay',
};
